// TemplateSelect.js — the "Get Started" onboarding step that sits between
// the Landing page and the Builder. Users acknowledge the T&C (once per
// session), pick an intent (Upload / Create), then browse the 9 templates
// and confirm one before entering the editor. The Builder itself is
// initialised from `?template=<id>&intent=<upload|create>` URL params and
// no longer defaults to "modern" for onboarding flows.
//
// Existing intra-Builder logic (Welcome dialog, T&C alert, template picker
// in the left pane) is left untouched — the Builder still supports direct
// navigation to `/builder` for returning users and deep-links.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, X } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import PrivacyAlert from "@/components/builder/PrivacyAlert";
import {
  TEMPLATE_LIST,
  TemplateThumbnail,
} from "@/components/builder/TemplatePicker";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const INTENT_UPLOAD = "upload";
const INTENT_CREATE = "create";

export default function TemplateSelect() {
  const nav = useNavigate();
  const [showPrivacyAlert, setShowPrivacyAlert] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [intent, setIntent] = useState(null); // 'upload' | 'create'
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Kick off the onboarding chain on mount:
  //   1) If T&C not acked → show PrivacyAlert.
  //   2) Otherwise open the Welcome dialog immediately.
  useEffect(() => {
    let ack = false;
    try {
      ack = sessionStorage.getItem("myresumedesign_privacy_ack") === "1";
    } catch (_) { /* SSR / private-mode fallback */ }
    if (!ack) {
      setShowPrivacyAlert(true);
    } else {
      setTimeout(() => setWelcomeOpen(true), 120);
    }
  }, []);

  const dismissPrivacyAlert = () => {
    try { sessionStorage.setItem("myresumedesign_privacy_ack", "1"); } catch (_) { /* ignore */ }
    setShowPrivacyAlert(false);
    setTimeout(() => setWelcomeOpen(true), 120);
  };

  const chooseIntent = (choice) => {
    try { sessionStorage.setItem("mrd_welcome_ack", "1"); } catch (_) { /* ignore */ }
    setIntent(choice);
    setWelcomeOpen(false);
  };

  // Re-opens the Welcome dialog so users can flip between Upload / Create
  // without having to leave the page (matches the "modular, maintainable"
  // acceptance criterion — this is a single-page onboarding surface).
  const changeIntent = () => setWelcomeOpen(true);

  const startEditing = () => {
    if (!selectedTemplateId || !intent) return;
    // Persist a lightweight breadcrumb so returning users get a subtle hint
    // that they came from onboarding (used nowhere critical yet — but
    // available for analytics or future personalisation).
    try { sessionStorage.setItem("mrd_onboarded_via_get_started", "1"); } catch (_) { /* ignore */ }
    nav(`/builder?template=${encodeURIComponent(selectedTemplateId)}&intent=${intent}`);
  };

  const intentLabel = useMemo(() => {
    if (intent === INTENT_UPLOAD) return "Upload existing résumé";
    if (intent === INTENT_CREATE) return "Create new résumé";
    return null;
  }, [intent]);

  const canStart = !!selectedTemplateId && !!intent;

  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans" data-testid="template-select-page">
      {/* Header — mirrors the app-wide chrome */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black" data-testid="ts-back-home">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <div className="h-4 w-px bg-black/20" />
            <Logo />
            <span className="label-mono ml-2">Get started</span>
          </div>
          {intentLabel && (
            <button
              type="button"
              onClick={changeIntent}
              className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-neutral-900"
              data-testid="ts-change-intent"
            >
              {intentLabel} · Change
            </button>
          )}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-14 pb-32">
        {/* Step indicator */}
        <div className="label-mono mb-4 flex items-center gap-3" data-testid="ts-step-indicator">
          <span className="inline-block w-6 h-px bg-black/40" />
          Step 1 of 2 · Pick a template
        </div>
        <h1 className="font-heading font-black tracking-tighter text-4xl lg:text-6xl leading-[0.98]">
          Choose the design.<br />
          <span className="shimmer-text">Then start writing.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-sm lg:text-base text-neutral-600 leading-relaxed">
          Every template is hand-tuned with its own typography and rhythm. Pick the one that
          matches the tone you want to hit — you can still switch inside the editor if you
          change your mind.
        </p>

        {/* Template gallery — dynamic scale via ResizeObserver in
            TemplateThumbnail means each card renders at whatever size the
            grid gives it, edge-to-edge. Wider cards on desktop → bigger,
            more readable previews without any CSS variable juggling. */}
        <div
          className="mt-10 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          data-testid="ts-gallery"
        >
          {TEMPLATE_LIST.map((t) => {
            const active = t.id === selectedTemplateId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedTemplateId(t.id)}
                className={`group text-left border-2 rounded-sm transition-all overflow-hidden ${
                  active
                    ? "border-[#2400FF] bg-[#2400FF]/[0.04] shadow-lg"
                    : "border-black/10 hover:border-black/40 hover:-translate-y-[2px]"
                }`}
                data-testid={`ts-template-${t.id}`}
                aria-pressed={active}
              >
                <div className="relative border-b border-black/5 bg-white">
                  <TemplateThumbnail Component={t.Component} sampleId={t.sample} />
                  {t.photo && (
                    <span className="absolute top-2 right-2 text-[9px] font-mono uppercase tracking-widest bg-[#2400FF] text-white px-1.5 py-0.5 z-10">
                      Photo
                    </span>
                  )}
                  {t.ats && (
                    <span className="absolute top-2 right-2 text-[9px] font-mono uppercase tracking-widest bg-black text-white px-1.5 py-0.5 z-10">
                      ATS
                    </span>
                  )}
                  {t.isFree && (
                    <span
                      className="absolute top-2 left-2 text-[9px] font-mono font-bold uppercase tracking-widest bg-emerald-500 text-white px-2 py-0.5 z-10 rounded-sm shadow-sm"
                      data-testid={`ts-template-${t.id}-free-badge`}
                      aria-label="Free download"
                    >
                      Free
                    </span>
                  )}
                  {active && (
                    <span
                      className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-[#2400FF] text-white px-2 py-1 rounded-sm z-10"
                      data-testid={`ts-template-${t.id}-selected-pill`}
                    >
                      <CheckCircle size={11} weight="fill" /> Selected
                    </span>
                  )}
                </div>
                <div className="px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className={`font-heading font-semibold text-sm tracking-tight ${active ? "text-[#2400FF]" : "text-neutral-950"}`}>
                      {t.name}
                    </div>
                    <div className="mt-0.5 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                      {t.tag}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full border transition-colors ${
                      active
                        ? "border-[#2400FF] bg-[#2400FF] text-white"
                        : "border-black/20 bg-white text-transparent group-hover:text-neutral-400"
                    }`}
                    aria-hidden="true"
                  >
                    <CheckCircle size={14} weight="fill" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Empty-state helper when nothing selected yet */}
        {!selectedTemplateId && intent && (
          <div className="mt-10 text-sm text-neutral-500" data-testid="ts-hint-select">
            Pick a template to unlock the <b className="text-neutral-800">Start editing</b> button.
          </div>
        )}
      </main>

      {/* Sticky CTA — always visible so users know exactly what happens next */}
      <div
        className="fixed bottom-0 inset-x-0 z-30 border-t border-black/10 bg-white/95 backdrop-blur-xl"
        data-testid="ts-sticky-cta"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs text-neutral-600">
            {selectedTemplateId ? (
              <span>
                <span className="text-neutral-400 mr-2">Selected template ·</span>
                <b className="text-neutral-950">
                  {TEMPLATE_LIST.find((t) => t.id === selectedTemplateId)?.name}
                </b>
                {intent && (
                  <>
                    <span className="text-neutral-400 mx-2">·</span>
                    <span className="font-mono uppercase tracking-widest">{intentLabel}</span>
                  </>
                )}
              </span>
            ) : (
              <span className="text-neutral-500">Choose a template above to continue.</span>
            )}
          </div>
          <button
            type="button"
            onClick={startEditing}
            disabled={!canStart}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            data-testid="ts-start-editing"
          >
            Start editing with this template
            <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* T&C alert — session-scoped, dismissed via existing PrivacyAlert */}
      {showPrivacyAlert && <PrivacyAlert onDismiss={dismissPrivacyAlert} />}

      {/* Welcome dialog — same copy & structure as the in-Builder dialog so
          the acceptance criterion "does not duplicate existing onboarding
          logic" holds visually. Users must pick an intent before the
          gallery becomes actionable. */}
      <AlertDialog
        open={welcomeOpen}
        onOpenChange={(o) => {
          // Do NOT allow dismissal without a choice — closing the dialog
          // without an intent leaves the sticky CTA disabled forever.
          if (o) setWelcomeOpen(true);
        }}
      >
        <AlertDialogContent className="max-w-lg" data-testid="ts-welcome-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight text-2xl">
              How would you like to start?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-neutral-700 pt-1">
              Upload an existing résumé to auto-extract your info, or start with a fresh canvas
              and fill in the fields yourself.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mt-2 space-y-3">
            <div className="border-2 border-[#2400FF] rounded-md p-4 bg-[#2400FF]/[0.03]">
              <div className="font-medium text-neutral-950 mb-1">Upload an existing résumé</div>
              <div className="text-[13px] text-neutral-600 leading-relaxed mb-3">
                We'll parse your PDF or DOCX and drop the extracted data into your chosen template.
              </div>
              <button
                type="button"
                onClick={() => chooseIntent(INTENT_UPLOAD)}
                className="w-full h-10 bg-[#2400FF] text-white text-sm font-medium hover:bg-[#2400FF]/90 transition-colors rounded-sm"
                data-testid="ts-welcome-upload"
              >
                Upload résumé
              </button>
            </div>

            <div className="border border-black/10 rounded-md p-4">
              <div className="font-medium text-neutral-950 mb-1">Create a new résumé</div>
              <div className="text-[13px] text-neutral-600 leading-relaxed mb-3">
                Start with a blank canvas and enter your details step by step — great for
                first-time job seekers or a fresh reset.
              </div>
              <button
                type="button"
                onClick={() => chooseIntent(INTENT_CREATE)}
                className="w-full h-10 border border-black/20 text-neutral-950 text-sm font-medium hover:bg-neutral-100 transition-colors rounded-sm"
                data-testid="ts-welcome-create"
              >
                Create new résumé
              </button>
            </div>
          </div>

          <div className="mt-2 text-[11px] text-neutral-500 text-center flex items-center justify-center gap-2">
            <span>You can switch templates any time inside the editor.</span>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
